import { useNavigation } from "../context/useNavigation";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../services/api";
import {
  LayoutDashboard,
  UserCog,
  Users,
  FileText,
  Wallet,
  Banknote,
  Car,
  BadgeDollarSign,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  UserX,
  TrendingUp,
  Gift,
  Coins,
  Star,
  ShieldCheck,
  BellRing,
  Search,
  MapPin,
} from "lucide-react";
import { useEffect } from "react";
import { useTheme } from "../context/ThemeContext";

const menuItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
  },
  {
    id: "drivers",
    label: "Rider management",
    icon: UserCog,
    path: "/drivers",
    // Detail pages live outside /drivers but belong to this section.
    alsoMatches: ["/driver-details"],
  },
  {
    id: "riders",
    label: "Passenger management",
    icon: Users,
    path: "/riders",
    alsoMatches: ["/rider-details"],
  },
  { id: "trips", label: "Trip Logs", icon: FileText, path: "/trips" },
  {
    id: "payments",
    label: "Payment & Earnings",
    icon: Wallet,
    path: "/payments",
  },
  {
    id: "withdrawals",
    label: "Withdrawals",
    icon: Banknote,
    path: "/withdrawals",
  },
  { id: "pricing", label: "Pricing", icon: BadgeDollarSign, path: "/pricing" },
  {
    id: "deletion-requests",
    label: "Deletion Requests",
    icon: UserX,
    path: "/deletion-requests",
  },
  { id: "fleet", label: "Fleet management", icon: Car, path: "/fleet" },
  {
    id: "rider-performance",
    label: "Rider Performance",
    icon: TrendingUp,
    path: "/rider-performance",
  },
  { id: "promotions", label: "Promotions", icon: Gift, path: "/promotions" },
  {
    id: "rider-rewards",
    label: "Rider Rewards",
    icon: Coins,
    path: "/rider-rewards",
  },
  { id: "ratings", label: "Ratings", icon: Star, path: "/ratings" },
  {
    id: "push-delivery",
    label: "Push delivery",
    icon: BellRing,
    path: "/push-delivery",
  },
  {
    id: "trip-inspector",
    label: "Trip inspector",
    icon: Search,
    path: "/trip-inspector",
  },
  {
    id: "service-areas",
    label: "Service areas",
    icon: MapPin,
    path: "/service-areas",
  },
  // Super-admin only — manage other admins.
  {
    id: "admins",
    label: "Admins",
    icon: ShieldCheck,
    path: "/admins",
    superAdminOnly: true,
  },
  { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
  { id: "logout", label: "Logout", icon: LogOut, isAction: true },
];

// Whole-segment prefix match, so "/riders" doesn't claim "/riders-x" but does claim "/riders/…".
const pathIsUnder = (pathname, base) =>
  pathname === base || pathname.startsWith(`${base}/`);

const findItemForPath = (pathname) =>
  menuItems.find(
    (item) =>
      item.path &&
      [item.path, ...(item.alsoMatches || [])].some((base) =>
        pathIsUnder(pathname, base),
      ),
  );

// The logged-in admin's role, read once from storage.
const getMyRole = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").role;
  } catch {
    return undefined;
  }
};

function Sidebar() {
  const { activeItem, setActiveItem, isSidebarOpen, setSidebarOpen } = useNavigation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Update active item based on current route
  useEffect(() => {
    const currentItem = findItemForPath(location.pathname);
    if (currentItem) {
      setActiveItem(currentItem.id);
    }
  }, [location.pathname, setActiveItem]);

  const closeSidebar = () => setSidebarOpen(false);
  const openSidebar = () => setSidebarOpen(true);

  const handleItemClick = async (item) => {
    if (item.isAction) {
      // Handle actions like logout
      if (item.id === "logout") {
        try {
          await logout();
          navigate("/");
        } catch (error) {
          console.error('Logout failed:', error?.message);
          // Even if logout API fails, still clear local storage and redirect
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          navigate("/");
        }
      }
    } else {
      setActiveItem(item.id);
      if (item.path) {
        navigate(item.path);
      }
    }
    closeSidebar();
  };

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-30 transition-opacity duration-300 lg:hidden ${
          isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeSidebar}
        aria-hidden={!isSidebarOpen}
      />

      {/* Mobile toggle button */}
      <button
        onClick={openSidebar}
        className={`fixed top-4 right-4 z-30 p-3 rounded-full shadow-lg bg-white text-gray-700 border border-gray-200 lg:hidden transition-transform duration-200 ${
          isSidebarOpen ? 'opacity-0 pointer-events-none -translate-y-2' : 'opacity-100 translate-y-0'
        }`}
        aria-label="Open navigation menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      <aside
        className={`fixed inset-y-0 left-0 w-64 shadow-lg flex flex-col z-40 transform transition-transform duration-300 ${
          theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'
        } ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        aria-label="Sidebar navigation"
      >
        {/* Logo */}
        <div className="pt-8 pl-6 pr-4 flex items-center justify-between">
          <img
            src="/ZY.svg"
            alt="zoooma logo"
            className="w-20 sm:w-24 lg:w-28 pb-6"
          />
          <button
            onClick={closeSidebar}
            className="lg:hidden p-2 rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-2">
          <ul className="space-y-1">
            {menuItems
              .filter(
                (item) =>
                  !item.superAdminOnly || getMyRole() === "super_admin",
              )
              .map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeItem === item.id && !item.isAction
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-orange-400 font-medium'
                        : 'bg-[#FFF5E6] text-[#FF8C00] font-medium'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-gray-600 hover:bg-gray-100'
                  } ${
                    item.id === "logout"
                      ? theme === 'dark'
                        ? 'text-red-400 hover:bg-gray-800'
                        : 'text-red-600 hover:bg-red-50'
                      : ""
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Theme toggle */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Theme
            </span>
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-full">
              <button
                onClick={() => toggleTheme('light')}
                className={`p-2 rounded-full transition-colors ${
                  theme === 'light'
                    ? 'bg-white text-orange-500 shadow-md'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                aria-label="Light mode"
              >
                <Sun className="w-4 h-4" />
              </button>
              <button
                onClick={() => toggleTheme('dark')}
                className={`p-2 rounded-full transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-orange-400 shadow-md'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                aria-label="Dark mode"
              >
                <Moon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;